import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadsService, LeadStats, LeadsFilter } from '../../services/leads.service'
import LeadCard from '../../components/leads/LeadCard'
import LeadDetailModal from '../../components/leads/LeadDetailModal'
import LeadFilters from '../../components/leads/LeadFilters'
import GenerateMessagesModal from '../../components/leads/GenerateMessagesModal'
import RulesPanel from '../../components/leads/RulesPanel'
import SendQueuePanel from '../../components/leads/SendQueuePanel'
import toast from 'react-hot-toast'

export default function LeadsPage() {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<LeadsFilter>({
    limit: 50,
    offset: 0,
  })
  const [selectedLead, setSelectedLead] = useState<number | null>(null)
  const [selectedLeads, setSelectedLeads] = useState<number[]>([])
  const [showGenerateModal, setShowGenerateModal] = useState(false)

  // Fetch leads
  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', filters],
    queryFn: () => leadsService.getLeads(filters),
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['leadStats'],
    queryFn: () => leadsService.getStats(),
  })

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (leadId: number) => leadsService.approveLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leadStats'] })
      toast.success('Лид одобрен')
    },
    onError: () => {
      toast.error('Ошибка при одобрении лида')
    },
  })

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (leadId: number) => leadsService.rejectLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leadStats'] })
      toast.success('Лид отклонён')
    },
    onError: () => {
      toast.error('Ошибка при отклонении лида')
    },
  })

  // Toggle selection
  const toggleSelection = (leadId: number) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    )
  }

  // Select all
  const selectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([])
    } else {
      setSelectedLeads(leads.map(l => l.id))
    }
  }

  // Bulk action mutation
  const bulkActionMutation = useMutation({
    mutationFn: ({ action }: { action: string }) =>
      leadsService.bulkAction(selectedLeads, action),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leadStats'] })
      setSelectedLeads([])
      toast.success(data.message)
    },
    onError: () => toast.error('Ошибка при выполнении действия'),
  })

  // Get approved leads for message generation
  const approvedLeads = leads.filter(l => l.status === 'approved')
  // Get leads with ready messages for bulk send
  const readyToSendLeads = leads.filter(l => l.status === 'message_generated')

  return (
    <div className="pt-14">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Лиды</h1>
          <p className="text-gray-600 mt-1">
            Управление найденными лидами
          </p>
        </div>
        <div className="flex gap-2">
          {readyToSendLeads.length > 0 && (
            <button
              onClick={() => {
                // Collect message IDs from leads with message_generated status
                // The actual message IDs come from lead detail, but for bulk send
                // we use the leads service send endpoint which accepts message_ids
                // We need to fetch lead details to get message IDs, or use a simpler approach
                const messageLeadIds = readyToSendLeads.map(l => l.id)
                // Generate messages first if needed, or directly send
                toast('Отправка запущена...', { icon: 'i' })
                // For now, use the generate-then-send flow
                // The actual message IDs will be fetched by the backend
                leadsService.sendMessages(messageLeadIds).then(data => {
                  queryClient.invalidateQueries({ queryKey: ['leads'] })
                  queryClient.invalidateQueries({ queryKey: ['sendQueue'] })
                  toast.success(data.message)
                }).catch(() => toast.error('Ошибка при отправке'))
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Отправить готовые ({readyToSendLeads.length})
            </button>
          )}
          {approvedLeads.length > 0 && (
            <button
              onClick={() => setShowGenerateModal(true)}
              className="btn-primary"
            >
              Сгенерировать ({approvedLeads.length})
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && <StatsBar stats={stats} />}

      {/* Send Queue Panel */}
      <SendQueuePanel />

      {/* Rules Panel */}
      <RulesPanel
        selectedRuleId={filters.rule_id}
        onSelectRule={(ruleId) => setFilters(f => ({ ...f, rule_id: ruleId, offset: 0 }))}
      />

      {/* Filters */}
      <LeadFilters
        filters={filters}
        onChange={setFilters}
      />

      {/* Bulk Actions */}
      {selectedLeads.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <span className="text-blue-800">
            Выбрано {selectedLeads.length} лидов
          </span>
          <div className="space-x-2">
            <button
              onClick={() => bulkActionMutation.mutate({ action: 'approve' })}
              disabled={bulkActionMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Одобрить
            </button>
            <button
              onClick={() => bulkActionMutation.mutate({ action: 'reject' })}
              disabled={bulkActionMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Отклонить
            </button>
            <button
              onClick={() => bulkActionMutation.mutate({ action: 'archive' })}
              disabled={bulkActionMutation.isPending}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              Архивировать
            </button>
            <button
              onClick={() => setSelectedLeads([])}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Снять выделение
            </button>
          </div>
        </div>
      )}

      {/* Leads List */}
      {leadsLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : leads.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Лиды не найдены
          </h3>
          <p className="text-gray-600">
            Добавьте каналы для мониторинга, чтобы начать находить лидов
          </p>
        </div>
      ) : (
        <>
          {/* Select All Checkbox */}
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedLeads.length === leads.length && leads.length > 0}
              onChange={selectAll}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="selectAll" className="ml-2 text-sm text-gray-700">
              Выбрать все
            </label>
          </div>

          {/* Leads Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {leads.map(lead => (
              <LeadCard
                key={lead.id}
                lead={lead}
                selected={selectedLeads.includes(lead.id)}
                onSelect={() => toggleSelection(lead.id)}
                onApprove={() => approveMutation.mutate(lead.id)}
                onReject={() => rejectMutation.mutate(lead.id)}
                onViewDetails={() => setSelectedLead(lead.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-6 flex justify-center space-x-2">
            <button
              onClick={() => setFilters(f => ({ ...f, offset: Math.max(0, (f.offset || 0) - (f.limit || 50)) }))}
              disabled={(filters.offset || 0) === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              Назад
            </button>
            <span className="px-4 py-2 text-gray-600">
              Страница {Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1}
            </span>
            <button
              onClick={() => setFilters(f => ({ ...f, offset: (f.offset || 0) + (f.limit || 50) }))}
              disabled={leads.length < (filters.limit || 50)}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              Вперёд
            </button>
          </div>
        </>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          leadId={selectedLead}
          onClose={() => setSelectedLead(null)}
        />
      )}

      {/* Generate Messages Modal */}
      {showGenerateModal && (
        <GenerateMessagesModal
          leads={approvedLeads}
          onClose={() => setShowGenerateModal(false)}
        />
      )}
    </div>
  )
}

function StatsBar({ stats }: { stats: LeadStats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
      <StatItem label="Всего" value={stats.total_leads} />
      <StatItem label="На рассмотрении" value={stats.pending_review} color="yellow" />
      <StatItem label="Одобрено" value={stats.approved} color="green" />
      <StatItem label="Отклонено" value={stats.rejected} color="red" />
      <StatItem label="Сообщения готовы" value={stats.message_generated} color="blue" />
      <StatItem label="Отправлено" value={stats.sent} color="purple" />
      <StatItem label="Ответили" value={stats.replied} color="emerald" />
    </div>
  )
}

function StatItem({
  label,
  value,
  color = 'gray',
}: {
  label: string
  value: number
  color?: string
}) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-50 text-gray-900',
    yellow: 'bg-yellow-50 text-yellow-900',
    green: 'bg-green-50 text-green-900',
    red: 'bg-red-50 text-red-900',
    blue: 'bg-blue-50 text-blue-900',
    purple: 'bg-purple-50 text-purple-900',
    emerald: 'bg-emerald-50 text-emerald-900',
  }

  return (
    <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
      <p className="text-xs opacity-75">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  )
}
